import React, { useState } from 'react';
import { Classe, Conta, VinculoPrestador, Condicoes, DocumentoAnexo } from './types';
import { Modal, Field, TextField, DateField, SelectField, CheckboxField, CnpjField, MoneyField, PercentField } from './fields';
import {
  Opt, TIPO_COTA, CONDOMINIO, STATUS_APLICACAO, BENCHMARK, TIPO_CONTA, PAPEL,
} from './mock';

type AbaClasse = 'identidade' | 'mercado' | 'condicoes' | 'flags';

export const ClasseSubform: React.FC<{ initial: Classe; onSave: (c: Classe) => void; onClose: () => void }> = ({ initial, onSave, onClose }) => {
  const [aba, setAba] = useState<AbaClasse>('identidade');
  const [c, setC] = useState<Classe>({ ...initial, condicoes: { ...initial.condicoes } });

  const set = (campo: keyof Classe, v: any) => setC((p) => ({ ...p, [campo]: v }));
  const setCond = (campo: keyof Condicoes, v: any) => setC((p) => ({ ...p, condicoes: { ...p.condicoes, [campo]: v } }));

  return (
    <Modal title={c.subclasse ? `Classe — ${c.subclasse}` : 'Nova classe'} onClose={onClose} onSave={() => onSave(c)}>
      <div className="fl-tabs">
        {(['identidade', 'mercado', 'condicoes', 'flags'] as AbaClasse[]).map((t) => (
          <button key={t} className={`fl-tab ${aba === t ? 'active' : ''}`} onClick={() => setAba(t)}>
            {t === 'identidade' ? 'Identificação' : t === 'mercado' ? 'Mercado' : t === 'condicoes' ? 'Condições' : 'Flags'}
          </button>
        ))}
      </div>

      {aba === 'identidade' && (
        <div className="fl-grid">
          <TextField label="Subclasse" required value={c.subclasse} onChange={(v) => set('subclasse', v)} placeholder="Única / Sênior / Subordinada"
            hint="Série/classe do fundo. Fundo simples usa 'Única'; FIDC tem Sênior, Mezanino e Subordinada (com condições diferentes)." />
          <CnpjField label="CNPJ da classe" value={c.cnpj_classe} onChange={(v) => set('cnpj_classe', v)}
            hint="CNPJ próprio da classe. A CVM 175 permite CNPJ por classe; preencha se a classe tiver o seu." />
          <TextField label="Nome" full value={c.nome} onChange={(v) => set('nome', v)} />
          <SelectField label="Tipo de cota" value={c.tipo_cota} onChange={(v) => set('tipo_cota', v)} options={TIPO_COTA}
            hint="Momento de cálculo da cota: Abertura (preço do início do dia) ou Fechamento (preço do fim do dia)." />
          <SelectField label="Condomínio" value={c.condominio} onChange={(v) => set('condominio', v)} options={CONDOMINIO}
            hint="Aberto: permite resgates a qualquer momento. Fechado: cotas resgatadas só no encerramento (típico de FIDC)." />
          <SelectField label="Status da aplicação" value={c.status_aplicacao} onChange={(v) => set('status_aplicacao', v)} options={STATUS_APLICACAO}
            hint="Situação da captação da classe: Aberto, Fechado ou em Abertura." />
          <TextField label="Público-alvo" value={c.publico_alvo} onChange={(v) => set('publico_alvo', v)} placeholder="Profissional / Qualificado / Geral"
            hint="Tipo de investidor autorizado a aplicar: Geral, Qualificado (R$ 1MM+) ou Profissional (R$ 10MM+)." />
          <TextField label="Calendário" value={c.calendario} onChange={(v) => set('calendario', v)}
            hint="Calendário de dias úteis usado nas regras de cotização/liquidação (ex.: ANBIMA)." />
        </div>
      )}

      {aba === 'mercado' && (
        <div className="fl-grid">
          <TextField label="Código CETIP" value={c.cod_cetip} onChange={(v) => set('cod_cetip', v)}
            hint="Código de registro da cota/ativo na CETIP (B3)." />
          <TextField label="ISIN" value={c.isin} onChange={(v) => set('isin', v)}
            hint="Código ISIN: identificador internacional do ativo/cota." />
          <SelectField label="Benchmark" value={c.benchmark} onChange={(v) => set('benchmark', v)} options={BENCHMARK}
            hint="Índice de referência da classe (ex.: CDI, CDI+, IPCA+)." />
          <PercentField label="Spread" value={c.spread} onChange={(v) => set('spread', v)}
            hint="Spread sobre o benchmark, em % (ex.: 2 = 2%)." />
          <TextField label="Rating" value={c.rating} onChange={(v) => set('rating', v)}
            hint="Classificação de risco da classe atribuída por agência (ex.: AAA, AA)." />
          <PercentField label="Razão de garantia" value={c.razao_garantia} onChange={(v) => set('razao_garantia', v)}
            hint="Nível de subordinação/garantia da classe, em % (ex.: 15 = 15%). Típico de FIDC." />
          <PercentField label="Razão de garantia (target)" value={c.razao_garantia_target} onChange={(v) => set('razao_garantia_target', v)}
            hint="Razão de garantia alvo (meta), em %." />
        </div>
      )}

      {aba === 'condicoes' && (
        <>
          <div className="fl-section-title">Taxas (% ao ano) e mínimos de taxa (R$)</div>
          <div className="fl-grid">
            <PercentField label="Tx. administração" value={c.condicoes.tx_adm} onChange={(v) => setCond('tx_adm', v)}
              hint="Taxa de administração ao ano, em % (ex.: 0,61)." />
            <PercentField label="Tx. performance" value={c.condicoes.tx_performance} onChange={(v) => setCond('tx_performance', v)}
              hint="Taxa de performance, em % (ex.: 20)." />
            <PercentField label="Tx. custódia" value={c.condicoes.tx_custodia} onChange={(v) => setCond('tx_custodia', v)} />
            <PercentField label="Tx. adm. fiduciária" value={c.condicoes.tx_adm_fiduciaria} onChange={(v) => setCond('tx_adm_fiduciaria', v)} />
            <MoneyField label="Tx. adm. mínima (R$)" value={c.condicoes.tx_adm_minima} onChange={(v) => setCond('tx_adm_minima', v)} />
            <MoneyField label="Mín. adm. fiduciário (R$)" value={c.condicoes.valor_minimo_adm_fiduciario} onChange={(v) => setCond('valor_minimo_adm_fiduciario', v)} />
          </div>
          <div className="fl-section-title">Valores mínimos (R$)</div>
          <div className="fl-grid">
            <MoneyField label="Valor 1ª cota" value={c.condicoes.vlr_primeira_cota} onChange={(v) => setCond('vlr_primeira_cota', v)} />
            <MoneyField label="Aplicação inicial" value={c.condicoes.aplicacao_inicial} onChange={(v) => setCond('aplicacao_inicial', v)} />
            <MoneyField label="Aplicação mínima" value={c.condicoes.aplicacao_minima} onChange={(v) => setCond('aplicacao_minima', v)} />
            <MoneyField label="Saldo mínimo" value={c.condicoes.saldo_minimo} onChange={(v) => setCond('saldo_minimo', v)} />
            <MoneyField label="Resgate mínimo" value={c.condicoes.resgate_minimo} onChange={(v) => setCond('resgate_minimo', v)} />
          </div>
          <div className="fl-section-title">Cotização / saída</div>
          <div className="fl-grid">
            <TextField label="Cotização aplicação" value={c.condicoes.d_cotizacao_aplic} onChange={(v) => setCond('d_cotizacao_aplic', v)} placeholder="d + 0du"
              hint="Prazo para converter a aplicação em cotas. Ex.: d + 0du = mesmo dia útil; du = dias úteis, dc = dias corridos." />
            <TextField label="Cotização resgate" value={c.condicoes.d_cotizacao_resgate} onChange={(v) => setCond('d_cotizacao_resgate', v)} placeholder="d + 180dc"
              hint="Prazo entre o pedido de resgate e o cálculo da cota. Ex.: d + 180dc = 180 dias corridos." />
            <TextField label="Pagamento resgate" value={c.condicoes.d_pgto_resgate} onChange={(v) => setCond('d_pgto_resgate', v)} placeholder="d + 1du"
              hint="Prazo entre a cotização do resgate e o crédito do dinheiro ao cotista. Ex.: d + 1du." />
            <MoneyField label="Taxa de saída (valor)" value={c.condicoes.taxa_saida_valor} onChange={(v) => setCond('taxa_saida_valor', v)} />
            <DateField label="Vigente desde" value={c.condicoes.vigente_desde} onChange={(v) => setCond('vigente_desde', v)}
              hint="Data a partir da qual estas condições passam a valer. Mudanças futuras geram uma nova versão (histórico preservado)." />
          </div>
          <div className="fl-checks" style={{ marginTop: 14 }}>
            <CheckboxField label="Possui taxa de saída" checked={c.condicoes.taxa_de_saida} onChange={(v) => setCond('taxa_de_saida', v)} />
          </div>
        </>
      )}

      {aba === 'flags' && (
        <>
          <div className="fl-grid">
            <DateField label="Início" value={c.dt_inicio} onChange={(v) => set('dt_inicio', v)} />
            <DateField label="Encerramento" value={c.dt_encerramento} onChange={(v) => set('dt_encerramento', v)} />
          </div>
          <div className="fl-checks" style={{ marginTop: 14 }}>
            <CheckboxField label="Classe ativa" checked={c.is_active} onChange={(v) => set('is_active', v)} />
            <CheckboxField label="Divulga no site" checked={c.divulgacao_site} onChange={(v) => set('divulgacao_site', v)} />
          </div>
        </>
      )}
    </Modal>
  );
};

export const ContaSubform: React.FC<{ initial: Conta; onSave: (c: Conta) => void; onClose: () => void }> = ({ initial, onSave, onClose }) => {
  const [c, setC] = useState<Conta>({ ...initial });
  const [erros, setErros] = useState<Record<string, string>>({});
  const set = (campo: keyof Conta, v: any) => setC((p) => ({ ...p, [campo]: v }));
  // tipo é NOT NULL + CHECK no banco; bloqueia salvar sem tipo
  const salvar = () => {
    if (!c.tipo) { setErros({ tipo: 'Selecione o tipo.' }); return; }
    setErros({});
    onSave(c);
  };
  return (
    <Modal title={c.tipo ? `Conta — ${c.tipo}` : 'Nova conta'} onClose={onClose} onSave={salvar}>
      <div className="fl-grid">
        <SelectField label="Tipo" required value={c.tipo} onChange={(v) => set('tipo', v)} options={TIPO_CONTA} error={erros.tipo} />
        <TextField label="Código" value={c.codigo} onChange={(v) => set('codigo', v)} />
        <TextField label="Banco" value={c.banco} onChange={(v) => set('banco', v)} placeholder="código do banco" />
        <TextField label="Agência" value={c.agencia} onChange={(v) => set('agencia', v)} />
        <TextField label="Número da conta" value={c.numero_conta} onChange={(v) => set('numero_conta', v)} />
        <TextField label="Favorecido" full value={c.favorecido} onChange={(v) => set('favorecido', v)} />
      </div>
    </Modal>
  );
};

export const DocumentoSubform: React.FC<{ initial: DocumentoAnexo; label: string; onSave: (d: DocumentoAnexo) => void; onClose: () => void }> = ({ initial, label, onSave, onClose }) => {
  const hoje = new Date().toISOString().slice(0, 10);
  const [d, setD] = useState<DocumentoAnexo>({
    ...initial,
    titulo: initial.tipo,                                // título = tipo do documento
    data_referencia: initial.data_referencia ?? hoje,    // default: data da importação
  });
  const [erro, setErro] = useState<string | undefined>();
  // sem arquivo não há versão válida (documento_versao.nome_arquivo/s3_* são NOT NULL)
  const salvar = () => {
    if (!d._file) { setErro('Selecione um arquivo.'); return; }
    setErro(undefined);
    onSave(d);
  };
  return (
    <Modal title={`Anexar — ${label}`} saveLabel="Anexar" onClose={onClose} onSave={salvar}>
      <div className="fl-grid">
        <Field label="Arquivo" required full error={erro}>
          <input
            type="file"
            className="fl-input fl-file"
            onChange={(e) => {
              const f = e.target.files && e.target.files[0] ? e.target.files[0] : undefined;
              setD((p) => ({ ...p, nome_arquivo: f?.name, _file: f }));
            }}
          />
        </Field>
      </div>
      {d.nome_arquivo && (
        <p style={{ fontSize: 12, color: '#6A6D70', marginTop: 12 }}>
          Arquivo selecionado: <strong>{d.nome_arquivo}</strong>
        </p>
      )}
    </Modal>
  );
};

export const PrestadorSubform: React.FC<{ initial: VinculoPrestador; options: Opt[]; onSave: (p: VinculoPrestador) => void; onClose: () => void }> = ({ initial, options, onSave, onClose }) => {
  const [p, setP] = useState<VinculoPrestador>({ ...initial });
  const [erros, setErros] = useState<Record<string, string>>({});
  const set = (campo: keyof VinculoPrestador, v: any) => setP((prev) => ({ ...prev, [campo]: v }));
  const escolherPrestador = (v: string) =>
    setP((prev) => ({
      ...prev,
      prestador_id: v ? Number(v) : undefined,
      prestador_nome: options.find((o) => o.value === v)?.label,
    }));
  const salvar = () => {
    const e: Record<string, string> = {};
    if (!p.prestador_id) e.prestador_id = 'Selecione o prestador.';
    if (!p.papel) e.papel = 'Selecione o papel.';
    if (!p.data_inicio) e.data_inicio = 'Informe a data de início.';
    setErros(e);
    if (Object.keys(e).length) return;
    onSave(p);
  };
  return (
    <Modal title="Vínculo de prestador" onClose={onClose} onSave={salvar}>
      <div className="fl-grid">
        <SelectField label="Prestador" required value={p.prestador_id ? String(p.prestador_id) : ''}
          onChange={escolherPrestador} options={options} error={erros.prestador_id}
          placeholder={options.length ? 'Selecione...' : 'Nenhum prestador cadastrado'} />
        <SelectField label="Papel" required value={p.papel} onChange={(v) => set('papel', v)} options={PAPEL} error={erros.papel} />
        <DateField label="Início" required value={p.data_inicio} onChange={(v) => set('data_inicio', v)} error={erros.data_inicio} />
        <DateField label="Fim" value={p.data_fim} onChange={(v) => set('data_fim', v)} />
      </div>
    </Modal>
  );
};
